import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, CloudOff, Cloud, Loader2, RefreshCw, 
  Folder, Image, Video, FileText, Trash2, ExternalLink,
  CheckCircle, AlertCircle, BarChart2, Activity, Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import apiClient from "@/utils/api";

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [driveStatus, setDriveStatus] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  
  // Analytics settings
  const [analyticsSettings, setAnalyticsSettings] = useState({
    ga4_measurement_id: '',
    meta_pixel_id: ''
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsSaving, setAnalyticsSaving] = useState(false);

  useEffect(() => {
    checkDriveStatus();
    loadAnalyticsSettings();
    
    // Check for OAuth callback results
    const driveConnected = searchParams.get('drive_connected');
    const driveError = searchParams.get('drive_error');
    
    if (driveConnected === 'true') {
      toast.success("Google Drive conectado exitosamente");
      // Clean URL
      navigate('/admin/settings', { replace: true });
    } else if (driveError) {
      if (driveError === 'scope') {
        toast.error("No se otorgaron los permisos necesarios");
      } else {
        toast.error("Error al conectar con Google Drive");
      }
      navigate('/admin/settings', { replace: true });
    }
  }, [searchParams, navigate]);

  const loadAnalyticsSettings = async () => {
    try {
      const response = await apiClient.get('/settings/analytics');
      setAnalyticsSettings(response.data);
    } catch (error) {
      console.error("Error loading analytics settings:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const saveAnalyticsSettings = async () => {
    setAnalyticsSaving(true);
    try {
      await apiClient.put('/settings/analytics', analyticsSettings);
      toast.success("Configuración de Analytics guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setAnalyticsSaving(false);
    }
  };

  const checkDriveStatus = async () => {
    try {
      const response = await apiClient.get('/drive/status');
      setDriveStatus({ ...response.data, loading: false });
      
      if (response.data.connected) {
        loadDriveFiles();
      }
    } catch (error) {
      setDriveStatus({ connected: false, loading: false });
    }
  };

  const connectDrive = async () => {
    setConnecting(true);
    try {
      const response = await apiClient.get('/drive/connect');
      if (response.data.authorization_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error("Error al iniciar conexión con Google Drive");
      setConnecting(false);
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm("¿Estás seguro de desconectar Google Drive? Los archivos ya subidos permanecerán en tu Drive.")) {
      return;
    }
    
    try {
      await apiClient.post('/drive/disconnect');
      toast.success("Google Drive desconectado");
      setDriveStatus({ connected: false, loading: false });
      setFiles([]);
    } catch (error) {
      toast.error("Error al desconectar Google Drive");
    }
  };

  const loadDriveFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await apiClient.get('/drive/files');
      setFiles(response.data.files || []);
    } catch (error) {
      console.error("Error loading files:", error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm("¿Eliminar este archivo de Google Drive?")) return;
    
    try {
      await apiClient.delete(`/drive/files/${fileId}`);
      toast.success("Archivo eliminado");
      setFiles(files.filter(f => f.id !== fileId));
    } catch (error) {
      toast.error("Error al eliminar archivo");
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image size={18} className="text-blue-400" />;
    if (mimeType?.startsWith('video/')) return <Video size={18} className="text-purple-400" />;
    if (mimeType === 'application/pdf') return <FileText size={18} className="text-red-400" />;
    if (mimeType?.includes('folder')) return <Folder size={18} className="text-yellow-400" />;
    return <FileText size={18} className="text-gray-400" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin/dashboard" className="text-[#A3A3A3] hover:text-white transition-colors">
            <ArrowLeft size={24} />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Configuración</h1>
            <p className="text-[#A3A3A3]">Administra las integraciones y configuración del sistema</p>
          </div>
        </div>

        {/* Google Drive Section */}
        <section className="bg-[#121212] border border-white/5 rounded-lg overflow-hidden mb-6">
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                <Cloud size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-white">Google Drive</h2>
                <p className="text-[#A3A3A3] text-sm">Almacena imágenes y documentos en la nube</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {driveStatus.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-[#D4AF37]" size={24} />
              </div>
            ) : driveStatus.connected ? (
              <div className="space-y-6">
                {/* Connected Status */}
                <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-green-500" size={20} />
                    <div>
                      <p className="text-white font-medium">Conectado</p>
                      <p className="text-[#A3A3A3] text-sm">
                        Los archivos se guardarán en tu Google Drive
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={disconnectDrive}
                    className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                  >
                    <CloudOff size={16} className="mr-2" />
                    Desconectar
                  </Button>
                </div>

                {/* Files List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-medium">Archivos en Drive</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={loadDriveFiles}
                      disabled={loadingFiles}
                      className="text-[#A3A3A3]"
                    >
                      <RefreshCw size={16} className={loadingFiles ? "animate-spin" : ""} />
                    </Button>
                  </div>

                  {loadingFiles ? (
                    <div className="text-center py-8 text-[#A3A3A3]">
                      <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                      Cargando archivos...
                    </div>
                  ) : files.length === 0 ? (
                    <div className="text-center py-8 text-[#A3A3A3]">
                      <Folder size={40} className="mx-auto mb-2 opacity-50" />
                      <p>No hay archivos en la carpeta de Ikonic Deals</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {files.map((file) => (
                        <div 
                          key={file.id}
                          className="flex items-center justify-between p-3 bg-black/30 rounded-lg hover:bg-black/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {getFileIcon(file.mimeType)}
                            <div className="min-w-0 flex-1">
                              <p className="text-white text-sm truncate">{file.name}</p>
                              <p className="text-[#A3A3A3] text-xs">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {file.webViewLink && (
                              <a 
                                href={file.webViewLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 text-[#A3A3A3] hover:text-white"
                              >
                                <ExternalLink size={16} />
                              </a>
                            )}
                            <button 
                              onClick={() => deleteFile(file.id)}
                              className="p-2 text-[#A3A3A3] hover:text-red-500"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CloudOff size={32} className="text-[#D4AF37]" />
                </div>
                <h3 className="text-white font-medium mb-2">Google Drive no conectado</h3>
                <p className="text-[#A3A3A3] text-sm mb-6 max-w-md mx-auto">
                  Conecta tu cuenta de Google para almacenar imágenes, videos y documentos de las propiedades directamente en Google Drive.
                </p>
                <Button 
                  onClick={connectDrive}
                  disabled={connecting}
                  className="bg-[#D4AF37] text-black hover:bg-[#FFE082] gap-2"
                >
                  {connecting ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Cloud size={18} />
                  )}
                  Conectar Google Drive
                </Button>
                
                {/* URI Information for Google Console */}
                <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-left max-w-lg mx-auto">
                  <p className="text-yellow-400 text-sm font-medium mb-3">Configuración requerida en Google Cloud Console:</p>
                  <ol className="text-[#A3A3A3] text-xs space-y-2 list-decimal list-inside mb-3">
                    <li>Ve a <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">Google Cloud Console → APIs & Services → Credentials</a></li>
                    <li>Selecciona tu <strong className="text-white">OAuth 2.0 Client ID</strong></li>
                    <li>En <strong className="text-white">"Authorized redirect URIs"</strong>, añade:</li>
                  </ol>
                  <code className="block bg-black/50 p-3 rounded text-[#D4AF37] text-sm break-all select-all cursor-pointer hover:bg-black/70 transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/api/drive/callback`);
                      toast.success('URI copiada al portapapeles');
                    }}
                    title="Click para copiar"
                  >
                    {window.location.origin}/api/drive/callback
                  </code>
                  <p className="text-[#525252] text-xs mt-2">Click en la URI para copiar</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Info Box */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-blue-400 shrink-0 mt-0.5" size={18} />
            <div className="text-sm">
              <p className="text-white font-medium mb-1">Beneficios de Google Drive</p>
              <ul className="text-[#A3A3A3] space-y-1 list-disc list-inside">
                <li>Almacenamiento en la nube sin límites de tu base de datos</li>
                <li>Acceso rápido a imágenes y documentos desde cualquier lugar</li>
                <li>Backup automático de todos tus archivos</li>
                <li>URLs públicas para compartir imágenes de propiedades</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Analytics Section */}
        <section className="bg-[#121212] border border-white/5 rounded-lg overflow-hidden mb-6" data-testid="analytics-settings">
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
                <BarChart2 size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-white">Analytics y Tracking</h2>
                <p className="text-[#A3A3A3] text-sm">Configura Google Analytics 4 y Meta Pixel</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
              </div>
            ) : (
              <>
                {/* Google Analytics 4 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Activity size={18} className="text-orange-400" />
                    <Label className="text-white font-medium">Google Analytics 4</Label>
                  </div>
                  <Input
                    value={analyticsSettings.ga4_measurement_id}
                    onChange={(e) => setAnalyticsSettings({
                      ...analyticsSettings,
                      ga4_measurement_id: e.target.value
                    })}
                    placeholder="G-XXXXXXXXXX"
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="ga4-input"
                  />
                  <p className="text-[#666] text-xs">
                    Encuentra tu ID de medición en Google Analytics → Admin → Streams de datos → Tu sitio web
                  </p>
                </div>

                {/* Meta Pixel */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-[18px] h-[18px] text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <Label className="text-white font-medium">Meta Pixel (Facebook)</Label>
                  </div>
                  <Input
                    value={analyticsSettings.meta_pixel_id}
                    onChange={(e) => setAnalyticsSettings({
                      ...analyticsSettings,
                      meta_pixel_id: e.target.value
                    })}
                    placeholder="XXXXXXXXXXXXXXXX"
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="meta-pixel-input"
                  />
                  <p className="text-[#666] text-xs">
                    Encuentra tu Pixel ID en Facebook Business Suite → Configuración de eventos → Píxeles
                  </p>
                </div>

                {/* Save Button */}
                <Button
                  onClick={saveAnalyticsSettings}
                  disabled={analyticsSaving}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#FFE082]"
                  data-testid="save-analytics-btn"
                >
                  {analyticsSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Guardar Configuración de Analytics
                </Button>

                {/* Events Info */}
                <div className="bg-[#0a0a0a] border border-white/5 rounded-lg p-4">
                  <p className="text-white font-medium mb-2">Eventos de conversión configurados:</p>
                  <ul className="text-[#A3A3A3] text-sm space-y-1">
                    <li>✓ <strong>generate_lead</strong> - Formulario de captura de leads</li>
                    <li>✓ <strong>contact</strong> - Clic en WhatsApp, teléfono o email</li>
                    <li>✓ <strong>view_item</strong> - Vista de propiedad</li>
                    <li>✓ <strong>page_view</strong> - Todas las páginas</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Google Search Console Section */}
        <section className="bg-[#121212] border border-white/5 rounded-lg overflow-hidden mb-6" data-testid="search-console-settings">
          <div className="p-6 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                  <polygon fill="#050505" points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-medium text-white">Google Search Console</h2>
                <p className="text-[#A3A3A3] text-sm">Verifica la propiedad de tu sitio en Google</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-[#A3A3A3]">
              Para verificar tu sitio en Google Search Console:
            </p>
            <ol className="text-[#A3A3A3] text-sm space-y-2 list-decimal list-inside">
              <li>Ve a <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">Google Search Console</a></li>
              <li>Agrega tu propiedad usando la URL completa de tu sitio</li>
              <li>Selecciona el método de verificación "Archivo HTML"</li>
              <li>Descarga el archivo HTML de verificación de Google</li>
              <li>Súbelo a la carpeta raíz de tu sitio o contáctanos para ayudarte</li>
            </ol>
            
            <div className="flex items-center gap-4 pt-4">
              <a 
                href="https://search.google.com/search-console" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Abrir Search Console
                </Button>
              </a>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <p className="text-amber-400 text-sm">
                <strong>Nota:</strong> El archivo de verificación <code className="bg-black/30 px-1 rounded">google-site-verification.html</code> 
                debe ser configurado manualmente con el código que te proporcione Google. Contáctanos si necesitas ayuda.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;
